import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useChartTheme } from '../../Dashboard/core/chartTheme'
import { DEPOSIT_GROUP_COLOR, PAYOUT_KIND_COLOR, PAYOUT_KIND_INK } from '../core/consts'
import { amountTicks, BAR_HEIGHT, Frame, layoutTimeline } from '../core/layout'
import { Viewport } from '../core/tooltip'
import {
  DepositGroup,
  PayoutKind,
  TimelineCandidate,
  TimelineDeposit,
  TimelineMerchant,
  TimelineMethod,
  TimelinePayout,
} from '../core/response.types'
import { BarRect } from '../core/types'
import DepositPopover from './DepositPopover'
import FloatingBox from './FloatingBox'
import PayoutTooltip from './PayoutTooltip'
import TimelineLegend from './TimelineLegend'

type Props = {
  fromMs: number
  toMs: number
  payouts: TimelinePayout[]
  deposits: TimelineDeposit[]
  merchants: TimelineMerchant[]
  methods: TimelineMethod[]
  loading: boolean
}

const HOUR_MS = 60 * 60 * 1000
const BASE_HEIGHT = 560
const MARGIN = { left: 72, right: 16, top: 12, bottom: 32 }
const MIN_PLOT_WIDTH = 1200
/** Ширина бара, начиная с которой номер выплаты помещается внутрь. */
const LABEL_MIN_WIDTH = 44

const hourLabel = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit',
})

const PAYOUT_KINDS: PayoutKind[] = ['rejected', 'unmatched', 'matched_unpaid', 'paid', 'live']
const DEPOSIT_GROUPS: DepositGroup[] = [
  'paid',
  'unpaid',
  'in_progress',
  'no_payout',
  'precheck_rejected',
]

type Pointer = { x: number; y: number }
type Selected = { deposits: TimelineDeposit[]; x: number; y: number }

const GROUP_PRIORITY: DepositGroup[] = [
  'paid',
  'unpaid',
  'in_progress',
  'no_payout',
  'precheck_rejected',
]

/** Цвет группы точек — по самому «продвинутому» исходу внутри неё. */
const dominantGroup = (deposits: TimelineDeposit[]): DepositGroup =>
  GROUP_PRIORITY.find((g) => deposits.some((d) => d.group === g)) ?? deposits[0].group

const toggled = <T,>(set: Set<T>, item: T): Set<T> => {
  const next = new Set(set)
  if (next.has(item)) next.delete(item)
  else next.add(item)
  return next
}

const TimelineCanvas: React.FC<Props> = ({
  fromMs,
  toMs,
  payouts: allPayouts,
  deposits: allDeposits,
  merchants,
  methods,
  loading,
}) => {
  // Легенда прячет слои графика; выбор живёт, пока открыта страница.
  const [hiddenKinds, setHiddenKinds] = useState<Set<PayoutKind>>(() => new Set())
  const [hiddenGroups, setHiddenGroups] = useState<Set<DepositGroup>>(() => new Set())
  const payouts = useMemo(
    () => allPayouts.filter((p) => !hiddenKinds.has(p.kind)),
    [allPayouts, hiddenKinds]
  )
  const deposits = useMemo(
    () => allDeposits.filter((d) => !hiddenGroups.has(d.group)),
    [allDeposits, hiddenGroups]
  )
  const theme = useChartTheme()
  const merchantById = useMemo(() => new Map(merchants.map((m) => [m.id, m.title])), [merchants])
  const methodById = useMemo(() => new Map(methods.map((m) => [m.id, m.name])), [methods])
  const merchantName = useCallback((id: number) => merchantById.get(id) || `#${id}`, [merchantById])
  const methodName = useCallback((id: number) => methodById.get(id) || `#${id}`, [methodById])
  const wrapRef = useRef<HTMLDivElement>(null)
  const [plotWidth, setPlotWidth] = useState(MIN_PLOT_WIDTH)
  const [hoverBar, setHoverBar] = useState<BarRect | null>(null)
  const [pointer, setPointer] = useState<Pointer>({ x: 0, y: 0 })
  const [selected, setSelected] = useState<Selected | null>(null)
  const [candidates, setCandidates] = useState<TimelineCandidate[]>([])

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setPlotWidth(Math.max(MIN_PLOT_WIDTH, entry.contentRect.width - MARGIN.left - MARGIN.right))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // A new window or filter makes the old selection point at something that may
  // no longer be drawn.
  useEffect(() => {
    setSelected(null)
    setCandidates([])
    setHoverBar(null)
  }, [fromMs, toMs, payouts, deposits])

  const maxAmount = useMemo(
    () => Math.max(1, ...payouts.map((p) => p.amount), ...deposits.map((d) => d.amount)),
    [payouts, deposits]
  )
  const ticks = useMemo(() => amountTicks(maxAmount), [maxAmount])
  const frame: Frame = useMemo(
    () => ({
      fromMs,
      toMs,
      width: plotWidth,
      baseHeight: BASE_HEIGHT,
      maxAmount: ticks[ticks.length - 1],
    }),
    [fromMs, toMs, plotWidth, ticks]
  )
  const layout = useMemo(() => layoutTimeline(payouts, deposits, frame), [payouts, deposits, frame])
  const points = layout.points
  const hours = useMemo(() => {
    const out: number[] = []
    for (let t = Math.ceil(fromMs / HOUR_MS) * HOUR_MS; t < toMs; t += HOUR_MS) out.push(t)
    return out
  }, [fromMs, toMs])

  const candidateByTicket = useMemo(
    () => new Map(candidates.map((c) => [c.matching_ticket_id, c])),
    [candidates]
  )
  const highlighting = selected !== null && candidates.length > 0

  const onCandidates = useCallback((list: TimelineCandidate[]) => setCandidates(list), [])
  const closePopover = useCallback(() => {
    setSelected(null)
    setCandidates([])
  }, [])

  const plotHeight = layout.height
  // Всплывашки держатся внутри ВИДИМОЙ части графика: по горизонтали полотно
  // прокручивается внутри обёртки, по вертикали — вместе со страницей, поэтому
  // граница по высоте — пересечение обёртки с окном браузера, а не всё полотно.
  const getViewport = useCallback((): Viewport => {
    const el = wrapRef.current
    if (!el) return { left: 0, top: 0, width: 0, height: 0 }
    const rect = el.getBoundingClientRect()
    const visibleTop = Math.max(0, -rect.top)
    const visibleBottom = Math.min(rect.height, window.innerHeight - rect.top)
    return {
      left: el.scrollLeft,
      top: visibleTop,
      width: el.clientWidth,
      height: Math.max(0, visibleBottom - visibleTop),
    }
  }, [])
  // Координаты курсора в системе содержимого обёртки — той же, в которой стоят всплывашки.
  const toWrap = useCallback((e: React.MouseEvent): Pointer => {
    const el = wrapRef.current
    const rect = el?.getBoundingClientRect()
    return {
      x: e.clientX - (rect?.left ?? 0) + (el?.scrollLeft ?? 0),
      y: e.clientY - (rect?.top ?? 0),
    }
  }, [])
  const onBarEnter = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      const bar = layout.bars[Number(e.currentTarget.dataset.i)]
      setHoverBar(bar ?? null)
      setPointer(toWrap(e))
    },
    [layout.bars, toWrap]
  )
  const onBarMove = useCallback(
    (e: React.MouseEvent<SVGGElement>) => setPointer(toWrap(e)),
    [toWrap]
  )
  const onBarLeave = useCallback(() => setHoverBar(null), [])
  // Бары не перерисовываются на каждое движение курсора: их тысячи, а меняется только позиция подсказки.
  const barElements = useMemo(
    () =>
      layout.bars.map((b, i) => {
        const candidate =
          b.payout.matching_ticket_id !== null
            ? candidateByTicket.get(b.payout.matching_ticket_id)
            : undefined
        const dimmed = highlighting && !candidate
        return (
          <g
            key={`b${i}`}
            opacity={dimmed ? 0.2 : 1}
            data-i={i}
            onMouseEnter={onBarEnter}
            onMouseMove={onBarMove}
            onMouseLeave={onBarLeave}
          >
            <rect
              x={b.x}
              y={b.y}
              width={b.width}
              height={BAR_HEIGHT}
              rx={2}
              fill={PAYOUT_KIND_COLOR[b.payout.kind]}
              stroke={candidate ? theme.dividerColor : 'none'}
              strokeWidth={candidate ? 2 : 0}
              strokeDasharray={candidate?.disqualified ? '4 2' : undefined}
            />
            {b.payout.payout_ticket_id !== null && b.width >= LABEL_MIN_WIDTH && (
              <text
                x={b.x + 4}
                y={b.y + BAR_HEIGHT - 2}
                fontSize={8}
                fill={PAYOUT_KIND_INK[b.payout.kind]}
                pointerEvents='none'
              >
                #{b.payout.payout_ticket_id}
              </text>
            )}
          </g>
        )
      }),
    [layout.bars, candidateByTicket, highlighting, theme, onBarEnter, onBarMove, onBarLeave]
  )

  const xPx = (ms: number) => ((ms - fromMs) / (toMs - fromMs)) * plotWidth
  const svgWidth = plotWidth + MARGIN.left + MARGIN.right
  const svgHeight = plotHeight + MARGIN.top + MARGIN.bottom

  return (
    <div className='card'>
      <div className='card-body position-relative'>
        <TimelineLegend
          payoutKinds={PAYOUT_KINDS}
          depositGroups={DEPOSIT_GROUPS}
          hiddenKinds={hiddenKinds}
          hiddenGroups={hiddenGroups}
          onToggleKind={(k) => setHiddenKinds((prev) => toggled(prev, k))}
          onToggleGroup={(g) => setHiddenGroups((prev) => toggled(prev, g))}
        />

        <div
          ref={wrapRef}
          className='position-relative'
          style={{ overflowX: 'auto' }}
          onClick={closePopover}
        >
          <svg
            width={svgWidth}
            height={svgHeight}
            role='img'
            aria-label='Таймлайн депозитов к выплатам'
          >
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {ticks.map((v) => {
                const y = layout.yOf(v) + BAR_HEIGHT / 2
                return (
                  <g key={`y${v}`}>
                    <line x1={0} x2={plotWidth} y1={y} y2={y} stroke={theme.gridColor} />
                    <text x={-8} y={y + 4} textAnchor='end' fontSize={10} fill={theme.mutedColor}>
                      {v.toLocaleString('ru-RU')}
                    </text>
                  </g>
                )
              })}
              {hours.map((t) => (
                <g key={`x${t}`}>
                  <line x1={xPx(t)} x2={xPx(t)} y1={0} y2={plotHeight} stroke={theme.gridColor} />
                  <text
                    x={xPx(t)}
                    y={plotHeight + 18}
                    textAnchor='middle'
                    fontSize={10}
                    fill={theme.mutedColor}
                  >
                    {hourLabel.format(new Date(t))}
                  </text>
                </g>
              ))}

              {barElements}

              {points.map((p, i) => {
                const isSelected = selected?.deposits === p.deposits
                const group = p.deposits.length > 1
                return (
                  <g
                    key={`p${i}`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelected({
                        deposits: p.deposits,
                        x: p.x + MARGIN.left,
                        y: p.y + MARGIN.top,
                      })
                    }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isSelected ? p.radius + 2 : p.radius}
                      fill={DEPOSIT_GROUP_COLOR[dominantGroup(p.deposits)]}
                      stroke={isSelected ? theme.dividerColor : theme.cardBg}
                      strokeWidth={isSelected ? 2 : 1}
                    />
                    {group && (
                      <text
                        x={p.x}
                        y={p.y + 3}
                        textAnchor='middle'
                        fontSize={8}
                        fontWeight={700}
                        fill='#FFFFFF'
                        pointerEvents='none'
                      >
                        {p.deposits.length > 99 ? '99+' : p.deposits.length}
                      </text>
                    )}
                  </g>
                )
              })}
            </g>
          </svg>

          {hoverBar && (
            <FloatingBox anchorX={pointer.x} anchorY={pointer.y} getViewport={getViewport}>
              <PayoutTooltip
                payout={hoverBar.payout}
                merchant={merchantName(hoverBar.payout.merchant_id)}
                method={methodName(hoverBar.payout.payment_method_id)}
                nowMs={toMs}
              />
            </FloatingBox>
          )}

          {selected && (
            <FloatingBox
              anchorX={selected.x}
              anchorY={selected.y}
              getViewport={getViewport}
              interactive
              zIndex={10}
              onClick={(e) => e.stopPropagation()}
            >
              <DepositPopover
                deposits={selected.deposits}
                merchantName={merchantName}
                methodName={methodName}
                onCandidates={onCandidates}
                onClose={closePopover}
              />
            </FloatingBox>
          )}
        </div>

        {loading && (
          <div
            className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded'
            style={{ background: theme.overlayBg, zIndex: 20 }}
          >
            <span className='d-inline-flex align-items-center gap-2'>
              <span className='spinner-border spinner-border-sm text-primary' role='status' />{' '}
              Загружаем окно…
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default TimelineCanvas
